import { useEffect, useRef, useMemo } from 'react'
import { useAuth } from '@/hooks/useAuth'
import useQueryGetUrgentNotification from '@/api/notification/getLiveEmergencyAlert.ts'
import useQueryGetAlertFallDetectionLive from '@/api/notification/getLiveFallDetectionAlert.ts'
import { isNotEmptyArray } from '@/util/array.ts'
import EmergencyAlertModal from '@/pages/notification/emergency/EmergencyAlertModal.tsx'
import { DISABLE_ALERTS_LIVE_FETCHING } from '@/constant/constant.ts'
import { EmergencyAlertType } from '@/api/notification/getLiveEmergencyAlert.ts'
import { EmergencyAlertConfirmStatus } from '@/api/notification/getNotificationSos.ts'
import { useDismissedAlertStore } from '@/store/DismissedAlertStore.ts'
import { useEmergencyAlertUIStore } from '@/store/EmergencyAlertUIStore.ts'
import { selectLatestUnprocessedAlert } from '@/util/emergencyAlertSelection.ts'

const GlobalAlertManager = () => {
    const { isLoggedIn } = useAuth()
    const { data: emergencyAlerts, refetch: refetchEmergency } = useQueryGetUrgentNotification(isLoggedIn)
    const { data: fallAlerts, refetch: refetchFall } = useQueryGetAlertFallDetectionLive(isLoggedIn)
    const { dismissedAlertIds } = useDismissedAlertStore()
    const openRequestToken = useEmergencyAlertUIStore(state => state.openRequestToken)
    const emergencyAlertDetailModalRef = useRef<any>()
    const lastAutoOpenedAlertRef = useRef<string>()
    const handledOpenRequestRef = useRef(0)

    const hasEmergencyAlerts = useMemo(() => {
        const unprocessed = emergencyAlerts?.emergencies?.filter(item => (item.alert.confirm === EmergencyAlertConfirmStatus.WAIT || item.alert.confirm === undefined))
        const notDismissed = unprocessed?.filter(item => !dismissedAlertIds.has(item.id))
        return isNotEmptyArray(notDismissed)
    }, [emergencyAlerts, dismissedAlertIds])

    const hasFallAlerts = useMemo(() => {
        const unprocessed = fallAlerts?.falls?.filter(item => (item.alert.confirm === EmergencyAlertConfirmStatus.WAIT || item.alert.confirm === undefined))
        const notDismissed = unprocessed?.filter(item => !dismissedAlertIds.has(item.id))
        return isNotEmptyArray(notDismissed)
    }, [fallAlerts, dismissedAlertIds])

    const latestUndismissedAlert = useMemo(() => selectLatestUnprocessedAlert(
        emergencyAlerts?.emergencies,
        fallAlerts?.falls,
        dismissedAlertIds
    ), [emergencyAlerts, fallAlerts, dismissedAlertIds])

    const latestAlert = useMemo(() => selectLatestUnprocessedAlert(
        emergencyAlerts?.emergencies,
        fallAlerts?.falls
    ), [emergencyAlerts, fallAlerts])

    useEffect(() => {
        if (DISABLE_ALERTS_LIVE_FETCHING) {
            return
        }

        if (latestUndismissedAlert) {
            const signature = `${latestUndismissedAlert.type}:${latestUndismissedAlert.id}:${latestUndismissedAlert.createdAt}`
            if (lastAutoOpenedAlertRef.current !== signature) {
                emergencyAlertDetailModalRef.current?.show(latestUndismissedAlert)
                lastAutoOpenedAlertRef.current = signature
            }
        }
    }, [latestUndismissedAlert])

    useEffect(() => {
        if (openRequestToken === 0 || handledOpenRequestRef.current === openRequestToken) return
        handledOpenRequestRef.current = openRequestToken
        emergencyAlertDetailModalRef.current?.show(latestAlert)
    }, [latestAlert, openRequestToken])


    const onModalClose = () => {
        refetchEmergency()
        refetchFall()
    }

    return (
        <>
            <EmergencyAlertModal
                ref={emergencyAlertDetailModalRef}
                refetchAlerts={onModalClose}
                defaultTab={hasFallAlerts && !hasEmergencyAlerts ? EmergencyAlertType.FALL : EmergencyAlertType.SOS}
                sosAlerts={emergencyAlerts}
                fallAlerts={fallAlerts}
            />
        </>
    )
}

export default GlobalAlertManager
